import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sortingOrder } from '../components/Tables/TablesList/Components/common';

type TableQueryStateOptions = {
	/**
	 * Identifies which table owns the current URL params. Several tables render
	 * under the same route (/home/documents), so params are only read back when
	 * the key matches; otherwise defaults apply and the next write clears them.
	 */
	tableKey: string;
	defaultSortBy: string;
	defaultOrder?: sortingOrder;
};

/**
 * Table list state (page, sort column, sort order, search filter) backed by
 * URL search params instead of component state. Because the state lives in the
 * URL, navigating to a detail page and coming back (browser back or Cancel)
 * restores the exact previous query.
 */
export function useTableQueryState({
	tableKey,
	defaultSortBy,
	defaultOrder = sortingOrder.DESCENDING
}: TableQueryStateOptions) {
	const [searchParams, setSearchParams] = useSearchParams();

	// Snapshot of the current params, kept in a ref so that two setter calls in
	// the same event (e.g. HeaderItem sets sortby then order) merge instead of
	// the second overwriting the first with stale params.
	const paramsRef = useRef<Record<string, string>>({});
	const snapshot: Record<string, string> = {};
	searchParams.forEach((value, key) => {
		snapshot[key] = value;
	});
	paramsRef.current = snapshot;

	const matchesTable = snapshot['t'] === tableKey;

	const rawPage = matchesTable ? parseInt(snapshot['page'] ?? '1', 10) : 1;
	const page = Number.isNaN(rawPage) || rawPage < 1 ? 1 : rawPage;

	const selectedHeader = (matchesTable && snapshot['sortby']) || defaultSortBy;

	const orderParam = matchesTable ? snapshot['order'] : null;
	const sortOrder =
		orderParam === sortingOrder.ASCENDING ||
		orderParam === sortingOrder.DESCENDING
			? orderParam
			: defaultOrder;

	const urlFilter = (matchesTable && snapshot['filter']) || '';

	const update = useCallback(
		(partial: Record<string, string | null>) => {
			// Drop params belonging to another table before writing ours.
			const next: Record<string, string> =
				paramsRef.current['t'] === tableKey ? { ...paramsRef.current } : {};
			next['t'] = tableKey;
			Object.entries(partial).forEach(([key, value]) => {
				if (value == null || value === '') delete next[key];
				else next[key] = value;
			});
			paramsRef.current = next;
			setSearchParams(next, { replace: true });
		},
		[tableKey, setSearchParams]
	);

	const setPage = useCallback(
		(nextPage: number) => update({ page: String(nextPage) }),
		[update]
	);
	const setSelectedHeader = useCallback(
		(header: string) => update({ sortby: header }),
		[update]
	);
	const setSortOrder = useCallback(
		(order: sortingOrder) => update({ order }),
		[update]
	);
	const setFilter = useCallback(
		(filter: string) => update({ filter: filter || null }),
		[update]
	);

	return {
		page,
		selectedHeader,
		sortOrder,
		urlFilter,
		setPage,
		setSelectedHeader,
		setSortOrder,
		setFilter
	};
}
